import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'underscore';
import classnames from 'classnames';

const inViewport = (element, scrollableRef) => {
  const { top, left } = element.getBoundingClientRect();
  const { right: parentRight } = scrollableRef && scrollableRef.current
    ? scrollableRef.current.getBoundingClientRect()
    : { right: window.innerWidth };

  // This function can be refined, left being > 0 does not mean it's visible
  // If parent bounding left is positive and there are elements scrolled to the left
  // Also done assuming our cases, scrollableRef only for horizontal lists
  return top >= 0 && left >= 0 && top <= window.innerHeight && left <= parentRight;
};

// Listeners object so only 1 DOM listener is created per element to listen
const listenersByScrollable = new Map();
const handlersByScrollable = new Map();

const lazyLoadHandler = (scrollable) => {
  if (!handlersByScrollable.has(scrollable)) {
    handlersByScrollable.set(scrollable, () => {
      const listeners = listenersByScrollable.get(scrollable);
      listeners.forEach((listener) => { listener(); });
    });
  }

  return handlersByScrollable.get(scrollable);
};

// Add event listeners for scrollables
// Create eventListener only if it does not exist
const addListeners = (scrollables, onScroll) => {
  scrollables.forEach((scrollable) => {
    if (!listenersByScrollable.has(scrollable)) {
      listenersByScrollable.set(scrollable, []);
      scrollable.addEventListener('scroll', lazyLoadHandler(scrollable));
    }

    listenersByScrollable.get(scrollable).push(onScroll);
  });
};

const removeListeners = (scrollables, onScroll) => {
  scrollables.forEach((scrollable) => {
    if (!listenersByScrollable.has(scrollable)) return;

    const listeners = listenersByScrollable.get(scrollable);

    const index = listeners.indexOf(onScroll);
    if (index !== -1) { listeners.splice(index, 1); }

    if (!listeners.length) {
      scrollable.removeEventListener('scroll', lazyLoadHandler(scrollable));
      listenersByScrollable.delete(scrollable);
    }
  });
};

const LazyImage = ({
  className,
  background,
  src,
  onLoad,
  onError,
  fallbackSrc,
  scrollableRef,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const loadIfVisible = () => {
      if (inViewport(ref.current, scrollableRef)) {
        const imageLoader = new Image();
        imageLoader.src = src;

        imageLoader.onload = () => {
          setLoaded(true);
          onLoad();
        };

        imageLoader.onerror = () => {
          setError(true);
          onError();
        };

        return true;
      }

      return false;
    };

    // If it not visible initially add scroll listeners
    if (!loadIfVisible()) {
      const scrollables = [
        window,
        scrollableRef && scrollableRef.current,
      ].filter(Boolean);

      const onScroll = () => { if (loadIfVisible()) removeListeners(scrollables, onScroll); };
      addListeners(scrollables, onScroll);

      return () => { removeListeners(scrollables, onScroll); };
    }

    return noop;
  }, []);

  const Tag = background ? 'div' : 'img';

  return (
    <Tag
      className={classnames(className, 'img-lazy', {
        'img-loaded': loaded,
        'img-error': error,
      })}
      style={background && loaded ? { backgroundImage: `url(${src})` } : {}}
      src={loaded ? src : (error && fallbackSrc)}
      ref={ref}
      {...rest}
    />
  );
};

LazyImage.propTypes = {
  src: PropTypes.string.isRequired,
  className: PropTypes.string,
  background: PropTypes.bool,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  fallbackSrc: PropTypes.string,
  scrollableRef: PropTypes.instanceOf(Object),
};

LazyImage.defaultProps = {
  className: '',
  background: false,
  onLoad: noop,
  onError: noop,
  fallbackSrc: '',
  scrollableRef: null,
};

export default LazyImage;
